/**
 * Demo mode: use the app without API / login for frontend-only testing.
 * Enable only via localStorage.demoMode = 'true' (e.g. from a "Try demo" action or dev tools).
 * Works on any local port (e.g. 3000, 3001); no dependency on a specific port.
 *
 * Three tiers:
 *   'worker'  — regular team member, sees personal schedule only
 *   'manager' — team manager (default), sees team management + personal schedule
 *   'full'    — admin/creator, all features enabled
 */

const DEMO_STORAGE_KEY = 'demoMode';
const DEMO_TIER_KEY = 'demoTier';
const DEMO_TOKEN = 'demo';
const DEMO_USER_ID = '1';

/** Valid demo tiers. */
export const DEMO_TIERS = ['worker', 'manager', 'full'];

export function isDemoMode() {
  return typeof localStorage !== 'undefined' && localStorage.getItem(DEMO_STORAGE_KEY) === 'true';
}

export function setDemoMode(enabled) {
  if (typeof localStorage === 'undefined') return;
  if (enabled) {
    localStorage.setItem(DEMO_STORAGE_KEY, 'true');
  } else {
    localStorage.removeItem(DEMO_STORAGE_KEY);
  }
}

/** Get the current demo tier. Defaults to 'manager'. */
export function getDemoTier() {
  if (typeof localStorage === 'undefined') return 'manager';
  const tier = localStorage.getItem(DEMO_TIER_KEY);
  return DEMO_TIERS.includes(tier) ? tier : 'manager';
}

/** Set the demo tier ('worker' | 'manager' | 'full'). */
export function setDemoTier(tier) {
  if (typeof localStorage === 'undefined') return;
  if (DEMO_TIERS.includes(tier)) {
    localStorage.setItem(DEMO_TIER_KEY, tier);
  }
}

/**
 * Competences for each tier.
 */
export function getDemoCompetences() {
  const tier = getDemoTier();
  switch (tier) {
    case 'worker':
      return { 101: 'cashier' };
    case 'full':
      return { 101: 'cashier', manager: 'manager', creator: 'creator' };
    case 'manager':
    default:
      return { 101: 'cashier', manager: 'manager' };
  }
}

/**
 * Feature flags per tier.
 */
function getDemoFeatures() {
  const tier = getDemoTier();
  const base = {
    needed_work: true,
    work_confirmation: true,
    time_tracking: true,
    leave: true,
    salary: true,
    swap_requests: true,
    swap_approval: true,
    schedule_templates: true,
    needed_work_templates: true,
    overtime_rules: true,
    recurring_shifts: true,
    recurring_needed_work: true,
    user_invoice: true,
    team_invoice: true,
    multiple_sites: true,
    competences: true,
    forced_competence: false,
    site_salary: false,
    show_needs_for_members: false,
    creator_is_worker: false,
    ai_scheduler: true,
  };
  if (tier === 'manager') {
    return { ...base, creator_is_worker: false, show_needs_for_members: true };
  }
  if (tier === 'full') {
    return { ...base, creator_is_worker: true, show_needs_for_members: true, site_salary: true, forced_competence: true };
  }
  return base;
}

/**
 * Set cookies so existing code that reads user_token / userId sees a valid "user".
 * Call when entering demo mode (e.g. from SignIn or Dashboard).
 */
export function setDemoCookies() {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setDate(expires.getDate() + 60);
  document.cookie = `user_token=${DEMO_TOKEN}; path=/; expires=${expires.toUTCString()}`;
  document.cookie = `userId=${DEMO_USER_ID}; path=/; expires=${expires.toUTCString()}`;
}

/**
 * Store minimal data so Dashboard and other pages that read from localStorage don't break.
 */
export function setDemoLocalStorage() {
  if (typeof localStorage === 'undefined') return;
  const teamId = '1';
  const teamsData = {
    [teamId]: {
      team_name: 'Brussels Central',
      competences: getDemoCompetences(),
      ConfirmationDate: '15',
    },
  };
  localStorage.setItem('userId', DEMO_USER_ID);
  localStorage.setItem('teams_and_competences', JSON.stringify({
    teams: teamsData,
  }));
  localStorage.setItem('selected_team', teamId);
  localStorage.setItem(`team_features_${teamId}`, JSON.stringify(getDemoFeatures()));
}

/**
 * Call on app load when demo mode is on: ensure cookies and localStorage are set
 * so the user is treated as "logged in".
 */
export function ensureDemoSession() {
  if (!isDemoMode()) return;
  setDemoCookies();
  setDemoLocalStorage();
}

/**
 * Fully clear all demo state: flag, cookies, and all localStorage keys set by demo mode.
 * Call when the user explicitly exits demo (e.g. "Create your account" CTA).
 */
export function clearDemoSession() {
  setDemoMode(false);
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('userId');
    localStorage.removeItem('teams_and_competences');
    localStorage.removeItem('selected_team');
    localStorage.removeItem('notification_token');
    localStorage.removeItem('team_features_1');
    localStorage.removeItem(DEMO_TIER_KEY);
  }
  if (typeof document !== 'undefined') {
    document.cookie = 'user_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'userId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
}
